import classNames from 'classnames'
import { BLACK_PEARL } from 'constants/colors'
import Icon from 'component_library/1_Atoms/Icon'
import './index.scss'
import { useMemo } from 'react'

type ListPaginationWithEllipsisProps = {
  isSEFriendly?: boolean
  currentPage: number
  changePage: (number: number) => void
  totalPages: number
  onNextPage: (offset?: number) => void
  onPreviousPage: (offset?: number) => void
}

const ListPaginationWithEllipsis: React.FC<ListPaginationWithEllipsisProps> = ({
  currentPage,
  changePage,
  totalPages,
  onNextPage,
  onPreviousPage,
  isSEFriendly = false,
}) => {
  const handleClick = (targetPageNumber: number) => {
    changePage(targetPageNumber)
  }

  const range = (start: number, end: number) => {
    const length = end - start + 1
    return Array.from({ length }, (_, idx) => idx + start)
  }

  const siblingCount = 1
  /*
  Number of sibling page numbers to show on either sides of the selected page 
  e.g. <Prev 1 ... 8 [9] 10 ... 21 Next> ---- siblingCount = 1
  e.g. <Prev 1 ... 7 8 [9] 10 11 ... 21 Next>  ----- siblingCount = 2
  */

  const ellipsis = '...'
  const paginationRange = useMemo(() => {
    // Pages count is determined as siblingCount + firstPage + lastPage + currentPage + 2*DOTS
    const totalPageNumbers = siblingCount + 5

    /*
      Case 1:
      If the number of pages is less than the page numbers we want to show in our
      paginationComponent, we return the range [1..totalPages]
    */
    if (totalPageNumbers >= totalPages) {
      return range(1, totalPages)
    }

    /*
    	Calculate left and right sibling index and make sure they are within range 1 and totalPages
    */
    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1)
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages)

    /*
      We do not show dots when there is just one page number to be inserted between the siblings of extremes and the page limits i.e 1 and totalPages. Hence we are using leftSiblingIndex > 2 and rightSiblingIndex < totalPages - 2
      eg: 1 2 [3] 4 5 - 3 being the number to be inserted
    */
    const shouldShowLeftDots = leftSiblingIndex > 2
    const shouldShowRightDots = rightSiblingIndex < totalPages - 2

    const firstPageIndex = 1
    const lastPageIndex = totalPages

    /*
    	Case 2: No left dots, only right dots
    */
    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + 2 * siblingCount
      const leftRange = range(1, leftItemCount)

      return [...leftRange, ellipsis, totalPages]
    }

    /*
    	Case 3: No right dots , only left dots.
    */
    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * siblingCount
      const rightRange = range(totalPages - rightItemCount + 1, totalPages)
      return [firstPageIndex, ellipsis, ...rightRange]
    }

    /*
    	Case 4: Both left and right dots to be shown
    */
    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex)
      return [firstPageIndex, ellipsis, ...middleRange, ellipsis, lastPageIndex]
    }
  }, [siblingCount, currentPage])

  return (
    totalPages > 1 && (
      <div
        className={classNames('PaginationContainer', {
          'PaginationContainer--seFriendly': isSEFriendly,
        })}
      >
        {currentPage !== 1 && (
          <button
            type="button"
            className={classNames(
              'btn btn-transparent',
              'PaginationContainer__navigationButtons',
              'PaginationContainer__navigationButtons--prev'
            )}
            onClick={() => onPreviousPage(currentPage - 1)}
          >
            <Icon icon="iosBackLeft" fill={BLACK_PEARL} className="fa-lg" />
            Prev
          </button>
        )}
        {paginationRange.map((page, index) => {
          const isCurrentPage = currentPage === Number(page)

          return (
            <button
              key={index}
              type="button"
              className="btn btn-transparent p-0"
              onClick={() => page !== ellipsis && handleClick(Number(page))}
            >
              <div
                className={classNames('PaginationContainer__paginationIndex', {
                  'PaginationContainer__paginationIndex--selected': isCurrentPage,
                })}
              >
                {page !== ellipsis ? Number(page) : page}
              </div>
            </button>
          )
        })}
        {Math.ceil(currentPage) !== totalPages && (
          <button
            type="button"
            className={classNames(
              'btn btn-transparent',
              'PaginationContainer__navigationButtons',
              'PaginationContainer__navigationButtons--next'
            )}
            onClick={() => onNextPage(currentPage - 1)}
          >
            Next
            <Icon icon="iosBackRight" fill={BLACK_PEARL} className="fa-lg" />
          </button>
        )}
      </div>
    )
  )
}

export default ListPaginationWithEllipsis
